<template>
    <div class="el-select el-select--small">
        <ElTooltip
            v-model:visible="show"
            trigger="click"
            placement="top-start"
            :teleported="false"
            :showArrow="false"
            effect="light"
            popperClass="el-select__popper claim-tree-selector__popper"
            pure
            :disabled="disabled"
            :popperOptions="{
                strategy: 'fixed',
            }"
        >
            <template #content>
                <div class="claim-tree-selector-content max-w-[50vw] min-w-[560px] w-fit overflow-x-auto py-2 space-y-2">
                    <div class="px-2 leading-4 space-x-1 text-xs w-full">
                        <span class="text-gray-450">{{ t('aiSpecification.claimTreeSelector.title') }}</span>
                        <span class="text-gray-60">({{ t('aiSpecification.claimTreeSelector.selected', { count: modelValue?.length ?? 0 }) }}):</span>
                    </div>
                    <ClaimTree
                        v-model="modelValue"
                        class="max-h-[320px] overflow-y-auto"
                        :claimTree="claimTree"
                        :depth="depth"
                    />
                </div>
            </template>
            <div
                class="el-select__wrapper el-tooltip__trigger el-tooltip__trigger"
                tabindex="-1"
                :class="{
                    'is-disabled': disabled,
                    'is-focused': show,
                }"
            >
                <div class="el-select__prefix">
                    <span class="text-gray-450">{{ t('aiSpecification.claimTreeSelector.claims') }}:</span>
                </div>
                <div class="el-select__selection py-0.5">
                    <div class="el-select__selected-item gap-1">
                        <span v-if="!modelValue.length" class="text-[#a8abb2]">{{ t('aiSpecification.claimTreeSelector.placeholder') }}</span>
                        <template v-else>
                            <span
                                v-for="item in sortedSelected"
                                :key="item.id"
                                class="text-white rounded px-1 leading-4 inline-flex items-center justify-center"
                                :class="{
                                    'bg-yellow-500': isRoot(item),
                                    'bg-gray-450': !isRoot(item),
                                }"
                            >
                                {{ item.num }}
                            </span>
                        </template>
                    </div>
                </div>
                <div class="el-select__suffix">
                    <i class="el-icon el-select__caret el-select__icon" :class="{ 'is-reverse': show }">
                        <SvgIcon icon="SolidArrowDown" />
                    </i>
                </div>
            </div>
        </ElTooltip>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { ElTooltip } from 'element-plus';
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import type { SpecificationConfirmClaimTree, SpecificationConfirmClaimItem } from '@/domains/specification/types/specification-confirm-claims';
import ClaimTree from './ClaimTree.vue';
import * as _ from 'lodash';
import '@patsnap-ui/icon/assets/solid/ArrowDown.svg';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
    claimTree: SpecificationConfirmClaimTree;
    disabled?: boolean;
}>();

const show = ref(false);

const depth = ref(0);

const modelValue = defineModel<SpecificationConfirmClaimItem[]>('modelValue', {
    default: () => [],
    required: true,
});

const sortedSelected = computed(() => {
    return _.sortBy([...modelValue.value], (item) => {
        return _.toNumber(item.num);
    });
});

const isRoot = (item: SpecificationConfirmClaimItem) => {
    return props.claimTree.includes(item);
};
</script>

<style lang="scss">
.claim-tree-selector__popper {
    border: 1px solid #bcc2cc !important;
}
</style>
